import React, { useEffect, useState } from 'react';
import { GraphvizDiagramCard } from '../C4DiagramCard/GraphvizDiagramCard';
import {
  Button,
  ButtonGroup,
  Menu,
  MenuItem,
  makeStyles,
} from '@material-ui/core';
import {
  Command,
  C4DiagramModel,
  C4DiagramEntity,
  C4DiagramRelation,
  Type,
} from '../C4DiagramCard/types';
import { useApi } from '@backstage/core-plugin-api';
import { modelApiRef } from '../../../services/ModelClient';
import { DialogRename } from './DialogRename';
import { DialogAdd } from './DialogAdd';
import { DialogDependency } from './DialogDependency';
// @ts-ignore
import * as uuid from 'uuid';
import Alert from '@material-ui/lab/Alert';
import { DialogEdit } from './DialogEdit';
import { DialogDelete, DialogResult, DialogSave } from '../../dialogs';

export interface C4ContainerDiagramCardProps {
  model: C4DiagramModel;
  components?: C4DiagramEntity[];
  relatedEntities?: C4DiagramEntity[];
}

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    '& > * + *': {
      marginTop: theme.spacing(2),
    },
  },
}));

export const C4ContainerDiagramCard = (props: C4ContainerDiagramCardProps) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>();
  const [openContextMenu, setOpenContextMenu] = useState(false);
  const [openDialogRename, setOpenDialogRename] = useState(false);
  const [openDialogAdd, setOpenDialogAdd] = useState(false);
  const [openDialogSave, setOpenDialogSave] = useState(false);
  const [openDialogEdit, setOpenDialogEdit] = useState(false);
  const [openDialogDelete, setOpenDialogDelete] = useState(false);
  const [openDialogResult, setOpenDialogResult] = useState(false);
  const [openDialogDependency, setOpenDialogDependency] = useState(false);
  const [dependsOn, setDependsOn] = useState(true);
  const [components, setComponents] = useState<Array<C4DiagramEntity>>([]);
  const [relations, setRelations] = useState<Array<C4DiagramEntity>>([]);
  const [relatedEntities, setRelatedEntities] = useState<
    Array<C4DiagramEntity>
  >([]);
  const [selectedEntity, setSelectedEntity] = useState<
    undefined | C4DiagramEntity
  >();
  const [selectedRelation, setSelectedRelation] = useState<
    undefined | C4DiagramRelation
  >();
  const [model, setModel] = useState(props.model);
  const [originalModel] = useState(JSON.parse(JSON.stringify(model)));
  const modelApi = useApi(modelApiRef);
  const [edit, setEdit] = useState(false);
  const [id, setId] = useState<undefined | string>();
  const [text, setText] = useState<undefined | string>();
  const [description, setDescription] = useState<undefined | string>();
  const [name, setName] = useState<undefined | string>();
  const [type, setType] = useState<undefined | string>();
  const [commands] = useState<Command[]>([]);
  const [mergeRequests, setMergeRequests] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  useEffect(() => {
    setModel(props.model);
  }, [props.model]);

  useEffect(() => {
    setRelatedEntities(props.relatedEntities ? props.relatedEntities : []);
  }, [props.relatedEntities]);

  useEffect(() => {
    setComponents(props.components ? props.components : []);
  }, [props.components]);

  const classes = useStyles();

  return (
    <>
      <div className={classes.root}>
        {isContainerDiagram() && edit && (
          <Alert severity="warning">
            You are now in the editing mode. You will loose all your changes if
            you leave this tab without saving!
          </Alert>
        )}
        {isContainerDiagram() && !edit && (
          <ButtonGroup variant="outlined">
            <Button onClick={onEdit}>Edit</Button>
          </ButtonGroup>
        )}
        {isContainerDiagram() && edit && (
          <ButtonGroup variant="outlined">
            <Button onClick={onSave}>Save</Button>
            <Button onClick={onCancel}>Cancel</Button>
          </ButtonGroup>
        )}
      </div>
      <GraphvizDiagramCard
        model={model}
        openContextMenu={handleOpenContextMenu}
        edit={edit}
      />
      <Menu
        anchorEl={anchorEl}
        open={openContextMenu}
        onClose={handleCloseContextMenu}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        onContextMenu={e => {
          e.preventDefault();
          handleCloseContextMenu();
          return false;
        }}
      >
        {isCluster() && isContainerDiagram() && (
          <MenuItem onClick={handleOpenDialogAdd}>
            Add existing container
          </MenuItem>
        )}
        {(isRelation() ||
          isContainer() ||
          isSystem() ||
          isProvider() ||
          isGroup()) &&
          isContainerDiagram() && (
            <MenuItem onClick={handleOpenDialogDelete}>Delete</MenuItem>
          )}
        {isContainer() && isContainerDiagram() && (
          <MenuItem onClick={handleOpenDialogEditContainer}>
            Edit container
          </MenuItem>
        )}
        {isContainer() && isContainerDiagram() && (
          <MenuItem onClick={handleOpenDialogNewContainer}>
            New container
          </MenuItem>
        )}
        {isContainer() && isContainerDiagram() && (
          <MenuItem onClick={handleOpenDialogNewUsedBy}>
            New usedBy relation
          </MenuItem>
        )}
        {isContainer() && isContainerDiagram() && (
          <MenuItem onClick={handleOpenDialogNewDependencyOf}>
            New dependencyOf relation
          </MenuItem>
        )}
        {isContainer() && isContainerDiagram() && (
          <MenuItem onClick={handleOpenDialogNewDependsOn}>
            New dependsOn relation
          </MenuItem>
        )}
        {isRelation() && isContainerDiagram() && (
          <MenuItem onClick={handleOpenDialogRename}>Edit relation</MenuItem>
        )}
      </Menu>
      <DialogSave
        open={openDialogSave}
        onClose={() => setOpenDialogSave(false)}
        onSave={handleSaveDialogSave}
      ></DialogSave>
      <DialogRename
        text={text}
        open={openDialogRename}
        onClose={() => setOpenDialogRename(false)}
        onSave={handleSaveDialogRename}
      ></DialogRename>
      <DialogAdd
        open={openDialogAdd}
        onClose={() => setOpenDialogAdd(false)}
        onSave={handleSaveDialogAdd}
        components={components}
      ></DialogAdd>
      <DialogEdit
        id={id}
        name={name}
        description={description}
        type={type}
        open={openDialogEdit}
        onClose={() => setOpenDialogEdit(false)}
        onSave={handleSaveDialogEdit}
      ></DialogEdit>
      <DialogDependency
        relations={relations}
        open={openDialogDependency}
        onClose={() => setOpenDialogDependency(false)}
        onSave={handleSaveDialogDependency}
      ></DialogDependency>
      <DialogDelete
        open={openDialogDelete}
        onNo={() => setOpenDialogDelete(false)}
        onYes={handleDialogDeleteYes}
      ></DialogDelete>
      <DialogResult
        open={openDialogResult}
        mergeRequests={mergeRequests}
        errorMessage={errorMessage}
        onClose={() => setOpenDialogResult(false)}
      ></DialogResult>
    </>
  );

  function isContainerDiagram() {
    return model.type === 'container';
  }

  function isCluster() {
    return anchorEl?.classList.value.includes('cluster');
  }

  function isContainer() {
    return (
      anchorEl?.classList.value.includes('node') &&
      !isSystem() &&
      !isGroup() &&
      !isProvider()
    );
  }

  function isSystem() {
    return anchorEl?.classList.value.includes('System');
  }

  function isProvider() {
    return anchorEl?.classList.value.includes('Provider');
  }

  function isGroup() {
    return anchorEl?.classList.value.includes('Group');
  }

  function isRelation() {
    return anchorEl?.classList.value.includes('edge');
  }

  function onEdit() {
    setEdit(true);
  }

  function onCancel() {
    setEdit(false);
    setModel({ ...originalModel });
  }

  function onSave() {
    setOpenDialogSave(true);
  }

  function handleSaveDialogEdit(
    id: string | undefined,
    text: string,
    type: string,
    description: string,
  ) {
    setOpenDialogEdit(false);
    if (id) {
      const command: Command = {
        action: 'change',
        location: selectedEntity!.location!,
        container: {
          id: selectedEntity!.id,
          type: type as Type,
          title: text,
          system: model.context!.id,
          owner: model.context!.owner,
          lifecycle: model.context!.lifecycle,
          description: description,
        },
      };
      commands.push(command);
      selectedEntity!.text = command.container!.title!;
      selectedEntity!.type = command.container!.type!;
      selectedEntity!.description = command.container!.description!;
    } else {
      const command: Command = {
        action: 'new',
        location: selectedEntity!.location!,
        container: {
          id: uuid.v4(),
          type: type as Type,
          title: text,
          system: model.context!.id,
          owner: model.context?.owner,
          lifecycle: model.context?.lifecycle,
          description: description,
        },
      };
      commands.push(command);
      model.context!.entities!.push({
        id: command.container!.id,
        external: false,
        context: false,
        type: command.container!.type,
        text: command.container!.title!,
        description: command.container!.description!,
        owner: command.container!.owner,
        lifecycle: command.container!.lifecycle,
        entities: [],
        location: command!.location,
      });
    }
  }

  function handleSaveDialogAdd(id: string) {
    setOpenDialogAdd(false);
    const component = components.find(it => it.id === id)!;
    const command: Command = {
      action: component.generated ? 'new' : 'change',
      location: component.location!,
      container: {
        id: component.generated ? uuid.v4() : component.id,
        type: component.type,
        title: component.text,
        system: model.context!.id,
        owner: model.context!.owner,
        lifecycle: model.context!.lifecycle,
        description: component.description,
      },
    };
    if (component.generated) {
      component.id = command.container!.id;
      setComponents(components.slice());
    }
    commands.push(command);
    model.context!.entities!.push(component);
  }

  function handleSaveDialogSave(
    branch: string | undefined,
    message: string | undefined,
  ) {
    setEdit(false);
    setOpenDialogSave(false);
    setMergeRequests([]);
    setErrorMessage(undefined);
    setOpenDialogResult(true);
    modelApi
      .postMergeCommands({
        commands: commands,
        branch: branch!,
        message: message!,
      })
      .then(result => {
        if (Array.isArray(result)) {
          setMergeRequests(result);
          setModel({ ...originalModel });
        } else {
          setErrorMessage(result);
        }
      });
  }

  function handleSaveDialogRename(text: string | undefined) {
    setOpenDialogRename(false);
    const relation = findRelation(model, selectedRelation?.id)!;
    const source = findEntity(model, selectedRelation?.source)!;
    const target = findEntity(model, selectedRelation?.target)!;
    const command: Command = {
      action: 'change',
      location:
        source.type === 'Group' ||
        source.type === 'System' ||
        source.type === 'Provider'
          ? target.location!
          : source.location!,
      relation: {
        id: relation.id,
        source: source.id,
        sourceType: source.type,
        target: target.id,
        targetType: target.type,
        description: text,
      },
    };
    commands.push(command);
    relation.text = text;
  }

  function handleSaveDialogDependency(id: string, description: string) {
    setOpenDialogDependency(false);
    let relatedEntity = relatedEntities.find(it => it.id === id);
    if (relatedEntity) {
      model.entities.push(relatedEntity);
    } else {
      relatedEntity = model.context?.entities?.find(it => it.id === id);
    }
    const command: Command = {
      action: 'new',
      location: selectedEntity!.location!,
      relation: {
        id: uuid.v4(),
        source: dependsOn ? selectedEntity!.id : relatedEntity!.id,
        sourceType: dependsOn ? selectedEntity!.type : relatedEntity!.type,
        target: dependsOn ? relatedEntity!.id : selectedEntity!.id,
        targetType: dependsOn ? relatedEntity!.type : selectedEntity!.type,
        description: description,
      },
    };
    commands.push(command);
    model.relations.push({
      id: command.relation!.id,
      source: command.relation!.source,
      target: command.relation!.target,
      text: command.relation!.description,
    });
  }

  function handleOpenContextMenu(e: HTMLElement) {
    setAnchorEl(e);
    setOpenContextMenu(true);
  }

  function handleOpenDialogAdd() {
    setSelectedEntity(findEntity(model, anchorEl?.id));
    setOpenDialogAdd(true);
    setOpenContextMenu(false);
  }

  function handleOpenDialogNewDependsOn() {
    const relations = model.context?.entities?.filter(
      it => it.id !== anchorEl?.id,
    );
    relatedEntities
      .filter(it => it.type !== 'Group')
      .forEach(it => relations?.push(it));
    setDependsOn(true);
    setSelectedEntity(findEntity(model, anchorEl?.id));
    setRelations(relations || []);
    setOpenDialogDependency(true);
    setOpenContextMenu(false);
  }

  function handleOpenDialogNewDependencyOf() {
    const relations = relatedEntities.filter(it => it.type === 'System');
    setDependsOn(false);
    setSelectedEntity(findEntity(model, anchorEl?.id));
    setRelations(relations || []);
    setOpenDialogDependency(true);
    setOpenContextMenu(false);
  }

  function handleOpenDialogNewUsedBy() {
    const relations = relatedEntities.filter(it => it.type === 'Group');
    setDependsOn(false);
    setSelectedEntity(findEntity(model, anchorEl?.id));
    setRelations(relations || []);
    setOpenDialogDependency(true);
    setOpenContextMenu(false);
  }

  function handleOpenDialogDelete() {
    if (isRelation()) {
      setSelectedEntity(undefined);
      setSelectedRelation(findRelation(model, anchorEl?.id));
    } else {
      setSelectedRelation(undefined);
      setSelectedEntity(findEntity(model, anchorEl?.id));
    }
    setOpenDialogDelete(true);
    setOpenContextMenu(false);
  }

  function handleDialogDeleteYes() {
    setOpenDialogDelete(false);
    const newModel = { ...model };
    if (isContainerDiagram()) {
      if (selectedEntity && newModel.context?.entities) {
        newModel.context.entities = newModel.context?.entities.filter(
          it => it.id !== selectedEntity.id,
        );
        newModel.relations = model.relations.filter(
          it =>
            it.source !== selectedEntity.id && it.target !== selectedEntity.id,
        );
        newModel.entities = model.entities.filter(it =>
          newModel.relations.find(
            rel => rel.source === it.id || rel.target === it.id,
          ),
        );
        setModel(newModel);
        const command: Command = {
          action: 'delete',
          location: selectedEntity!.location!,
          container: {
            id: selectedEntity.id,
            type: selectedEntity.type,
            owner: selectedEntity.owner,
            lifecycle: selectedEntity.lifecycle,
          },
        };
        commands.push(command);
      } else if (
        selectedEntity?.type === 'Group' ||
        selectedEntity?.type === 'Provider' ||
        selectedEntity?.type === 'System'
      ) {
        const deletedRelations = model.relations.filter(
          it =>
            it.source === selectedEntity.id || it.target === selectedEntity.id,
        );
        deletedRelations.forEach(relation => {
          const source = findEntity(model, relation.source)!;
          const target = findEntity(model, relation.target)!;
          const command: Command = {
            action: 'delete',
            location:
              source.type === 'System' ||
              source.type === 'Group' ||
              source.type === 'Provider'
                ? target.location!
                : source.location!,
            relation: {
              id: relation.id,
              source: source.id,
              sourceType: source.type,
              target: target.id,
              targetType: target.type,
            },
          };
          commands.push(command);
        });
        newModel.entities = model.entities.filter(
          it => it.id !== selectedEntity.id,
        );
        newModel.relations = model.relations.filter(
          it =>
            it.source !== selectedEntity.id && it.target !== selectedEntity.id,
        );
        setModel(newModel);
      } else if (selectedRelation) {
        const source = findEntity(model, selectedRelation.source)!;
        const target = findEntity(model, selectedRelation.target)!;
        const command: Command = {
          action: 'delete',
          location:
            source.type === 'System' ||
            source.type === 'Group' ||
            source.type === 'Provider'
              ? target.location!
              : source.location!,
          relation: {
            id: selectedRelation.id,
            source: source.id,
            sourceType: source.type,
            target: target.id,
            targetType: target.type,
          },
        };
        commands.push(command);
        newModel.relations = model.relations.filter(
          it => it.id !== selectedRelation.id,
        );
        newModel.entities = model.entities.filter(it =>
          newModel.relations.find(
            rel => rel.source === it.id || rel.target === it.id,
          ),
        );
        setModel(newModel);
      }
    }
    setOpenContextMenu(false);
  }

  function handleOpenDialogNewContainer() {
    setSelectedEntity(findEntity(model, anchorEl?.id));
    setId(undefined);
    setName('');
    setType('Database');
    setDescription('');
    setOpenDialogEdit(true);
    setOpenContextMenu(false);
  }

  function handleOpenDialogEditContainer() {
    const res = findEntity(model, anchorEl?.id);
    setSelectedEntity(res);
    setId(res?.id);
    setName(res?.text);
    setType(res?.type);
    setDescription(res?.description);
    setOpenDialogEdit(true);
    setOpenContextMenu(false);
  }

  function handleOpenDialogRename() {
    setSelectedEntity(undefined);
    const res = findRelation(model, anchorEl?.id);
    setSelectedRelation(res);
    setText(res?.text);
    setOpenDialogRename(true);
    setOpenContextMenu(false);
  }

  function handleCloseContextMenu() {
    setAnchorEl(null);
    setOpenContextMenu(false);
  }

  function findRelation(
    contextModel: C4DiagramModel,
    id: string | undefined,
  ): C4DiagramRelation | undefined {
    return contextModel.relations.find(it => it.id === id);
  }

  function findEntity(
    contextModel: C4DiagramModel,
    id: string | undefined,
  ): C4DiagramEntity | undefined {
    if (contextModel.context) {
      if (contextModel.context.id === id) {
        return contextModel.context;
      } else if (contextModel.context.entities) {
        const entity = contextModel.context.entities.find(it => it.id === id);
        if (entity) {
          return entity;
        }
      }
    }
    return contextModel.entities.find(it => it.id === id);
  }
};
